/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: DialRestrictionSelect.tsx
 🗓️ Created: 2025-01-14
 🧩 Description:
    Custom dial restriction selection component with search functionality.

 🔧 Notes:
    - Implements searchable dropdown
    - Supports keyboard navigation
    - Loads restrictions from API

 🔁 Last Updated: 2025-01-14 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import React, { useState, useMemo, useRef } from 'react';
import { ChevronDown, Search, Shield } from 'lucide-react';

interface DialRestrictionOption {
  id: number;
  name: string;
  description?: string;
}

interface DialRestrictionSelectProps {
  value: number | null;
  onChange: (value: number | null) => void;
  options: DialRestrictionOption[];
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
}

export const DialRestrictionSelect: React.FC<DialRestrictionSelectProps> = ({
  value,
  onChange,
  options,
  className = '',
  placeholder = 'No Dial Restriction',
  disabled = false,
  id
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Filter options based on search
  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) {
      return options;
    }
    
    const query = searchQuery.toLowerCase();
    return options.filter(option => 
      option.name.toLowerCase().includes(query) ||
      (option.description && option.description.toLowerCase().includes(query))
    );
  }, [searchQuery, options]);

  // Get selected label
  const selectedLabel = useMemo(() => {
    if (!value) return placeholder;
    const selected = options.find(option => option.id === value);
    return selected ? selected.name : placeholder;
  }, [value, placeholder, options]);

  // Handle selection
  const handleSelect = (restrictionId: number | null) => {
    onChange(restrictionId);
    setIsOpen(false);
    setSearchQuery('');
  };

  // Handle keyboard
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchQuery('');
    }
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        id={id}
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
      >
        <div className="flex items-center gap-2">
          <Shield className="h-4 w-4 text-muted-foreground" />
          <span className={value ? 'text-foreground' : 'text-muted-foreground'}>
            {selectedLabel}
          </span>
        </div>
        <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-border bg-popover shadow-lg">
          {/* Search Input */}
          <div className="p-2 border-b border-border">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Search..."
                className="w-full pl-8 pr-3 py-1.5 text-sm rounded-md border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                autoFocus
              />
            </div>
          </div>

          {/* Options List */}
          <div className="max-h-60 overflow-y-auto">
            {/* Default Option */}
            <button
              type="button"
              onClick={() => handleSelect(null)}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground ${
                !value ? 'bg-accent' : ''
              }`}
            >
              <div className="font-medium">{placeholder}</div>
              <div className="text-xs text-muted-foreground">No dial restrictions</div>
            </button>

            {/* Restriction Options */}
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => handleSelect(option.id)}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground ${
                    value === option.id ? 'bg-accent' : ''
                  }`}
                >
                  <div className="font-medium">{option.name}</div>
                  {option.description && (
                    <div className="text-xs text-muted-foreground">{option.description}</div>
                  )}
                </button>
              ))
            ) : (
              <div className="px-3 py-2 text-sm text-muted-foreground">
                No restrictions found
              </div>
            )}
          </div>
        </div>
      )}

      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => {
            setIsOpen(false);
            setSearchQuery('');
          }}
        />
      )}
    </div>
  );
};

export default DialRestrictionSelect;

